/* =====================================================================
 * ONE 플랫폼 공통 UI — 대화상자 · 모달 규칙 (TS 사본)            원본 버전 20261022
 *
 * 원본: 99.OnePlatform/public/js/one-ui.js + css/one-ui.css (쉘에 한 벌만 둔다)
 * 이 파일은 그 원본을 TS 모듈로 **옮긴 것**이다. 규칙·동작은 원본과 같게 둔다.
 * 원본 버전이 오르면 원본 diff 를 보고 여기도 같이 고친 뒤 머리 주석의 버전을 올린다.
 * CSS 는 src/styles/one-ui.css 에 원본을 그대로 복사했다(main.tsx 에서 자기 CSS 뒤에 import).
 *
 * DOM 에 직접 그리므로 React 밖(라우터의 나가기 가드 등)에서도 쓴다.
 *   if (!(await OneUI.confirm('지울까요?', { danger: true }))) return;
 *
 * [무엇을 주나] OneUI
 *   alert(message, {title, okText, tone})                         → Promise<void>
 *   confirm(message, {title, okText, cancelText, danger})         → Promise<boolean>
 *   prompt(message, {title, defaultValue, placeholder, required}) → Promise<string|null>
 *   guardBackdrop(backEl, onClose)       배경 닫힘을 한 줄로 바꿔 끼울 때
 *   bindModal(backEl, {onClose, input, isDirty})  이미 있는 모달을 아래 규칙에 묶을 때
 *   confirmDiscard()                     '저장하지 않고 닫을까요?'
 *   matchKo(query, text) · hasJamo(query) · personLabel(name, dept)
 *   hasOpen()                            지금 떠 있는 모달이 있는가
 *
 * [규칙] — NEW_SYSTEM_INTEGRATION_PROMPT.md 13절과 같다
 *   ① 배경을 눌러 닫는 것은 정보 모달만이다. 입력 모달·확인창은 배경으로 닫지 않는다.
 *   ② 입력 모달은 ✕·취소·Esc 에서 바뀐 내용이 있으면 '저장하지 않고 닫을까요?' 를 묻는다.
 *   ③ 배경 닫힘은 누른 곳과 뗀 곳이 **둘 다** 배경일 때만 일어난다.
 *   ④ Esc 는 맨 위 모달 하나만 닫는다(가장 먼저 듣고 전파를 끊는다).
 *      → 이 모듈은 main.tsx 맨 앞에서 불러, 다른 window keydown 보다 먼저 듣게 한다.
 *   ⑤ Enter 는 포커스된 버튼을 따른다. 한글 조합 중의 Enter 는 무시한다.
 *   ⑥ 모달이 겹치면 나중 것이 z-index 10000 이상으로 올라선다.
 *   ⑦ 모양은 class 와 CSS 파일로만 준다 — style="" 을 innerHTML 로 넣지 않는다(CSP).
 *   ⑧ 닫히면 열기 전 포커스로 돌아간다. role="dialog"·aria-modal, 터치도 같은 규칙.
 * ===================================================================== */

export const ONE_UI_VERSION = '20261022';

const Z_BASE = 10000; // 편집기 패널·메뉴보다 위
const Z_STEP = 10;
// 창을 연 직후에 들어온 '키보드 click' 은 이 창에서 누른 키가 아니면 버린다.
// 입력칸의 keydown 에서 곧바로 확인창을 띄우면, 같은 Enter 의 keypress 가
// 새로 포커스된 '확인' 을 눌러 버린다(크롬은 Enter 를 keypress 에서 누른다).
const ARM_MS = 1000;

type Initial = HTMLElement | null | (() => HTMLElement | null);

interface Layer {
  back: HTMLElement;
  box: HTMLElement;
  own: boolean;
  open: boolean;
  armed: boolean;
  trap: boolean;
  initial: Initial;
  restoreTo: HTMLElement | null;
  seq?: number;
  openedAt?: number;
  escape: () => void;
}

// ── 층(떠 있는 모달) 목록 ──────────────────────────────────────────
// OneUI 가 띄운 창과 bindModal 로 묶은 기존 모달이 함께 들어간다.
// '맨 위' 는 화면에 실제로 위에 그려진 것이다 — z-index 가 크고, 같으면 나중에 연 것.
const layers: Layer[] = [];
let seq = 0;
let uid = 0;

function attached(el: Element | null): boolean {
  return !!el && el.isConnected;
}
function shown(el: Element | null): boolean {
  return attached(el) && el!.getClientRects().length > 0;
}
function zOf(el: Element): number {
  const z = parseInt(window.getComputedStyle(el).zIndex, 10);
  return isNaN(z) ? 0 : z;
}

// 다른 코드가 모달을 DOM 에서 바로 지워 버린 경우(React 언마운트 등)를 정리한다.
function prune(): void {
  for (let i = layers.length - 1; i >= 0; i--) {
    if (!attached(layers[i]!.back)) {
      layers[i]!.open = false;
      layers.splice(i, 1);
    }
  }
}
function topLayer(): Layer | null {
  prune();
  let best: Layer | null = null;
  let bestZ = 0;
  for (const l of layers) {
    if (!shown(l.back)) continue; // 감춰 둔 모달은 빼고
    const z = zOf(l.back);
    if (!best || z > bestZ || (z === bestZ && (l.seq ?? 0) > (best.seq ?? 0))) {
      best = l;
      bestZ = z;
    }
  }
  return best;
}
function highestZ(except?: Layer): number {
  prune();
  let m = 0;
  for (const l of layers) {
    if (l !== except && shown(l.back)) m = Math.max(m, zOf(l.back));
  }
  return m;
}
function pushLayer(l: Layer): void {
  dropLayer(l);
  l.seq = ++seq;
  layers.push(l);
}
function dropLayer(l: Layer): void {
  const i = layers.indexOf(l);
  if (i >= 0) layers.splice(i, 1);
}

// ── 포커스 ────────────────────────────────────────────────────────
const FOCUSABLE =
  'a[href],button,input,select,textarea,iframe,[tabindex],[contenteditable="true"]';

function focusables(root: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [];
  root.querySelectorAll<HTMLElement>(FOCUSABLE).forEach((el) => {
    if ((el as HTMLButtonElement).disabled || el.tabIndex < 0) return;
    if ((el as HTMLInputElement).type === 'hidden') return;
    if (!el.getClientRects().length) return;
    out.push(el);
  });
  return out;
}
function focusEl(el: HTMLElement | null): boolean {
  if (!el || typeof el.focus !== 'function') return false;
  try {
    el.focus({ preventScroll: true });
  } catch {
    try {
      el.focus();
    } catch {
      return false;
    }
  }
  return document.activeElement === el;
}
function focusInto(l: Layer): void {
  const t = typeof l.initial === 'function' ? l.initial() : l.initial;
  if (t && l.box.contains(t) && focusEl(t)) return;
  focusEl(l.box);
}
// 열기 전 자리로 돌아간다. 그 자리가 사라졌으면(목록을 다시 그렸다 등) 아래 남은 모달로.
function restoreFocus(el: HTMLElement | null): void {
  if (el && el !== document.body && shown(el) && focusEl(el)) return;
  const t = topLayer();
  if (t) focusInto(t);
}
function markDialog(box: HTMLElement): void {
  if (!box.hasAttribute('role')) box.setAttribute('role', 'dialog');
  box.setAttribute('aria-modal', 'true');
  if (!box.hasAttribute('tabindex')) box.setAttribute('tabindex', '-1');
  box.classList.add('oui-focus-host'); // 상자 자체에 포커스가 가도 테두리를 그리지 않는다
}

// 배경으로 못 닫는 창에서 배경을 누르면 살짝 흔들어 '창이 떠 있다' 고 알린다.
function nudge(box: HTMLElement): void {
  box.classList.remove('oui-nudge');
  void box.offsetWidth; // 다시 붙여도 애니메이션이 처음부터 돌게
  box.classList.add('oui-nudge');
  window.setTimeout(() => box.classList.remove('oui-nudge'), 320);
}

// ── 키보드 · 포커스 — 문서 전체에서 한 번만 듣는다 ───────────────────
// window 의 capture 단계에서 가장 먼저 듣는다. 편집기의 Esc(명령 취소)나 뒤쪽 모달이
// 먼저 돌면 '위 창만 닫기' 가 깨진다.
function composing(e: KeyboardEvent): boolean {
  return e.isComposing || e.keyCode === 229;
}

function trapTab(e: KeyboardEvent, l: Layer): void {
  const list = focusables(l.box);
  const a = document.activeElement as HTMLElement | null;
  if (!list.length) {
    e.preventDefault();
    focusEl(l.box);
    return;
  }
  const first = list[0]!;
  const last = list[list.length - 1]!;
  if (!a || !l.box.contains(a) || a === l.box) {
    e.preventDefault();
    focusEl(e.shiftKey ? last : first);
  } else if (e.shiftKey && a === first) {
    e.preventDefault();
    focusEl(last);
  } else if (!e.shiftKey && a === last) {
    e.preventDefault();
    focusEl(first);
  }
}

window.addEventListener(
  'keydown',
  (e) => {
    if (!layers.length) return;
    const l = topLayer();
    if (!l) return;
    const target = e.target as Node | null;
    const inside = !!target && l.back.contains(target);
    const key = e.key;

    if (key === 'Escape' || key === 'Esc') {
      if (composing(e)) return; // 한글 조합을 취소하는 Esc 는 입력기 몫
      if (!inside && !l.trap) return; // 포커스를 풀어 둔 모달 밖의 Esc
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (!e.repeat) l.escape(); // 꾹 누르고 있어도 한 겹만 닫는다
      return;
    }
    if (!l.trap) return;
    if (key === 'Tab') {
      trapTab(e, l);
      return;
    }
    if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
      if (!inside) {
        // 모달 뒤 화면으로 키가 새지 않게 — 뒤에 남은 버튼이 눌려 같은 창이 또 뜨는 일을 막는다.
        e.preventDefault();
        e.stopPropagation();
        focusInto(l);
        return;
      }
      if (e.repeat) {
        // 누르고 있는 키의 반복 입력으로 OneUI 버튼이 눌리지 않게
        if (l.own && (e.target as HTMLElement).tagName === 'BUTTON') e.preventDefault();
        return;
      }
      l.armed = true; // 이 창 안에서 누른 키 — 이제 버튼이 눌려도 된다
    }
  },
  true
);

// aria-modal: 맨 위 모달 밖으로 포커스가 나가면 되돌린다.
window.addEventListener(
  'focusin',
  (e) => {
    if (!layers.length) return;
    const l = topLayer();
    if (!l || !l.trap || l.back.contains(e.target as Node)) return;
    focusInto(l);
  },
  true
);

// ── ③ 배경 닫힘 — 누른 곳과 뗀 곳이 둘 다 배경일 때만 ─────────────────
// [왜] click 은 누른 요소와 뗀 요소의 '공통 조상' 에서 일어난다. 모달 안에서 눌러
// 글자를 고르다 배경에서 떼면 공통 조상이 배경이라 e.target === 배경 이 참이 된다.
const HAS_POINTER = typeof window.PointerEvent === 'function';

/**
 * 이미 있는 모달의 배경 닫힘을 이것으로 바꾼다.
 * @returns 해제 함수
 */
function guardBackdrop(back: HTMLElement, onClose: (e: Event) => void): () => void {
  let downOnBack = false;
  let upOnBack = false;
  const down = (e: Event) => {
    downOnBack = e.target === back;
    upOnBack = false;
  };
  const up = (e: Event) => {
    upOnBack = e.target === back;
  };
  const click = (e: Event) => {
    const ok = downOnBack && upOnBack && e.target === back;
    downOnBack = upOnBack = false;
    if (ok) onClose(e);
  };
  const evDown = HAS_POINTER ? 'pointerdown' : 'mousedown';
  const evUp = HAS_POINTER ? 'pointerup' : 'mouseup';
  // capture 로 듣는다 — 안쪽 요소가 전파를 끊어도 '안에서 눌렀다' 는 것은 알아야 한다.
  back.addEventListener(evDown, down, true);
  back.addEventListener(evUp, up, true);
  if (!HAS_POINTER) {
    back.addEventListener('touchstart', down, true);
    back.addEventListener('touchend', up, true);
  }
  back.addEventListener('click', click);
  return () => {
    back.removeEventListener(evDown, down, true);
    back.removeEventListener(evUp, up, true);
    if (!HAS_POINTER) {
      back.removeEventListener('touchstart', down, true);
      back.removeEventListener('touchend', up, true);
    }
    back.removeEventListener('click', click);
  };
}

// ── 입력 모달의 '바뀐 내용' 판정 ────────────────────────────────────
/** 안에 있는 입력칸 값을 한 줄로 묶는다. 열 때 값과 닫을 때 값이 다르면 바뀐 것이다. */
function formState(root: HTMLElement): string {
  const parts: string[] = [];
  root
    .querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      'input, select, textarea'
    )
    .forEach((el) => {
      if (el.hasAttribute('data-oui-skip')) return; // 검색칸처럼 '내용' 이 아닌 칸
      const t = (el.type || '').toLowerCase();
      if (t === 'button' || t === 'submit' || t === 'reset' || t === 'image') return;
      if (t === 'checkbox' || t === 'radio') parts.push((el as HTMLInputElement).checked ? '1' : '0');
      else if (el instanceof HTMLSelectElement && el.multiple) {
        parts.push(
          Array.from(el.options)
            .filter((o) => o.selected)
            .map((o) => o.value)
            .join(',')
        );
      } else parts.push(el.value);
    });
  return parts.join('\u0001');
}

export interface BindModalOptions {
  /** 실제로 닫는 일. 필수 */
  onClose: () => void;
  /** 입력 모달 — 배경으로 닫지 않고, 닫을 때 바뀐 것이 있으면 묻는다 */
  input?: boolean;
  /** 생략하면 열 때와 지금의 입력칸 값을 견준다 */
  isDirty?: () => boolean;
  /** 모달 상자 요소. 생략하면 back 의 첫 자식 */
  dialog?: HTMLElement;
  initialFocus?: Initial;
  /** false 면 Esc 로 닫지 않는다(흔들기만) */
  escClose?: boolean;
  /** false 면 정보 모달이라도 배경으로 닫지 않는다 */
  backdropClose?: boolean;
  /** false 면 포커스를 가두지 않는다 */
  trapFocus?: boolean;
}

export interface ModalHandle {
  /** ✕·취소 버튼에 건다. 바뀐 것이 있으면 묻고 닫는다 */
  requestClose(): Promise<boolean>;
  /** 저장에 성공했을 때처럼 묻지 않고 닫는다 */
  close(): void;
  /** 감춰 두고 다시 쓰는 모달은 보이게 한 뒤 부른다 */
  open(): ModalHandle;
  /** 내용을 늦게 그리는 모달은 다 그린 뒤 부른다(그때 값이 기준이 된다) */
  markClean(): void;
  isDirty(): boolean;
  isOpen(): boolean;
}

/** 이미 있는 모달을 위 규칙에 묶는다. 만들자마자 화면에 보이는 모달은 곧바로 open() 까지 한다. */
function bindModal(back: HTMLElement, opts: BindModalOptions): ModalHandle {
  const box = opts.dialog || (back.firstElementChild as HTMLElement | null) || back;
  const input = !!opts.input;
  const l: Layer = {
    back,
    box,
    own: false,
    open: false,
    armed: true,
    trap: opts.trapFocus !== false,
    initial: opts.initialFocus || null,
    restoreTo: null,
    escape: () => {},
  };
  let baseline: string | null = null;
  let asking = false;

  function dirty(): boolean {
    if (typeof opts.isDirty === 'function') return !!opts.isDirty();
    return input && baseline !== null && formState(box) !== baseline;
  }

  const h: ModalHandle = {
    open() {
      if (!l.open) {
        l.open = true;
        const a = document.activeElement as HTMLElement | null;
        l.restoreTo = a && a !== document.body && !back.contains(a) ? a : null;
        baseline = input ? formState(box) : null;
      }
      // ⑥ 다른 모달 위에 열리면 그보다 올라선다
      const m = highestZ(l);
      if (m && m >= zOf(back)) back.style.zIndex = String(Math.max(Z_BASE, m + Z_STEP));
      pushLayer(l);
      if (!box.contains(document.activeElement)) focusInto(l);
      return h;
    },
    close() {
      if (!l.open) return;
      l.open = false;
      dropLayer(l);
      const a = document.activeElement;
      const focusWasHere = !a || a === document.body || back.contains(a);
      const to = l.restoreTo;
      l.restoreTo = null;
      opts.onClose();
      if (focusWasHere) restoreFocus(to);
    },
    requestClose() {
      if (!l.open || asking) return Promise.resolve(false);
      if (!dirty()) {
        h.close();
        return Promise.resolve(true);
      }
      asking = true;
      return confirmDiscard().then((ok) => {
        asking = false;
        if (ok) h.close();
        return ok;
      });
    },
    markClean() {
      baseline = input ? formState(box) : null;
    },
    isDirty: dirty,
    isOpen: () => l.open && shown(back),
  };

  l.escape = () => {
    if (opts.escClose === false) {
      nudge(box);
      return;
    }
    void h.requestClose();
  };
  guardBackdrop(back, () => {
    if (!l.open) return;
    if (input || opts.backdropClose === false) {
      nudge(box); // ① 입력 모달은 안 닫는다
      return;
    }
    void h.requestClose();
  });
  markDialog(box);
  if (shown(back)) h.open();
  return h;
}

// ── OneUI 자체 창 (alert · confirm · prompt) ─────────────────────────
type Tone = 'ok' | 'bad' | 'warn' | 'info' | 'ask';
const ICON: Record<Tone, string> = { ok: '✓', bad: '!', warn: '!', info: 'i', ask: '?' };

function make<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (cls) el.className = cls;
  if (text != null) el.textContent = text; // 언제나 글자로 넣는다
  return el;
}

interface DialogConfig {
  kind: 'alert' | 'confirm' | 'prompt';
  role: string;
  tone?: Tone | null;
  title?: string;
  message?: string;
  okText?: string;
  cancelText?: string;
  okClass?: string;
  focusCancel?: boolean;
  field?: {
    value?: string;
    placeholder?: string;
    numeric?: boolean;
    maxLength?: number;
    required?: boolean;
    requiredText?: string;
  };
}

function openDialog(cfg: DialogConfig): Promise<unknown> {
  return new Promise((resolve) => {
    const id = ++uid;
    const back = make('div', 'oui-back');
    const box = make('div', 'oui-dialog oui-dialog--' + cfg.kind);
    box.setAttribute('role', cfg.role);
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('tabindex', '-1');

    if (cfg.tone && ICON[cfg.tone]) {
      const ic = make('div', 'oui-icon oui-icon--' + cfg.tone, ICON[cfg.tone]);
      ic.setAttribute('aria-hidden', 'true');
      box.appendChild(ic);
    }
    let labelId: string | null = null;
    if (cfg.title) {
      const t = make('h2', 'oui-title', String(cfg.title));
      t.id = labelId = 'oui-t' + id;
      box.appendChild(t);
    }
    const text = cfg.message == null ? '' : String(cfg.message);
    if (text) {
      const m = make('div', 'oui-msg', text);
      m.id = 'oui-m' + id;
      box.appendChild(m);
      if (labelId) box.setAttribute('aria-describedby', m.id);
      else labelId = m.id;
    }
    if (labelId) box.setAttribute('aria-labelledby', labelId);

    let field: HTMLInputElement | null = null;
    let err: HTMLDivElement | null = null;
    let initialValue = '';
    if (cfg.field) {
      const f = cfg.field;
      field = make('input', 'oui-input');
      field.type = 'text';
      field.setAttribute('autocomplete', 'off');
      if (f.numeric) field.setAttribute('inputmode', 'numeric');
      if (f.placeholder) field.placeholder = String(f.placeholder);
      if (f.maxLength) field.maxLength = f.maxLength;
      initialValue = f.value == null ? '' : String(f.value);
      field.value = initialValue;
      if (labelId) field.setAttribute('aria-labelledby', labelId);
      box.appendChild(field);
      err = make('div', 'oui-err');
      err.setAttribute('role', 'alert');
      box.appendChild(err);
    }

    const l: Layer = {
      back,
      box,
      own: true,
      open: true,
      armed: false,
      trap: true,
      openedAt: Date.now(),
      restoreTo: null,
      initial: null,
      escape: () => {},
    };
    let settled = false;
    let asking = false;

    function finish(value: unknown): void {
      if (settled) return;
      settled = true;
      l.open = false;
      dropLayer(l);
      const a = document.activeElement;
      const focusWasHere = !a || a === document.body || back.contains(a);
      back.remove();
      if (focusWasHere) restoreFocus(l.restoreTo);
      resolve(value);
    }
    function showErr(msg: string): void {
      err!.textContent = msg;
      err!.classList.add('oui-on');
      field!.classList.add('oui-bad');
      focusEl(field);
    }
    function ok(): void {
      if (!field) {
        finish(cfg.kind === 'confirm' ? true : undefined);
        return;
      }
      const v = field.value;
      if (cfg.field!.required && !v.trim()) {
        showErr(cfg.field!.requiredText || '내용을 입력해 주세요.');
        return;
      }
      finish(v);
    }
    function cancel(): void {
      if (cfg.kind === 'alert') {
        finish(undefined);
        return;
      }
      // ② 적은 것이 있으면 버리기 전에 묻는다
      if (field && field.value !== initialValue) {
        if (asking) return;
        asking = true;
        void confirmDiscard().then((yes) => {
          asking = false;
          if (yes) finish(null);
        });
        return;
      }
      finish(field ? null : false);
    }

    const bar = make('div', 'oui-actions');
    function button(label: string, cls: string, act: () => void): HTMLButtonElement {
      const b = make('button', 'oui-btn' + (cls ? ' oui-btn--' + cls : ''), label);
      b.type = 'button';
      b.addEventListener('click', (e) => {
        // 이 창을 연 Enter 가 곧바로 이 버튼을 누르지 않게(키보드로 눌린 click 은 detail 이 0)
        if (e.isTrusted && e.detail === 0 && !l.armed && Date.now() - (l.openedAt ?? 0) < ARM_MS)
          return;
        act();
      });
      bar.appendChild(b);
      return b;
    }
    const cancelBtn = cfg.kind === 'alert' ? null : button(cfg.cancelText || '취소', '', cancel);
    const okBtn = button(cfg.okText || '확인', cfg.okClass || 'primary', ok);
    box.appendChild(bar);
    back.appendChild(box);

    if (field) {
      const fld = field;
      fld.addEventListener('keydown', (e) => {
        // ⑤ 한글 조합 중의 Enter 는 글자를 확정하는 것이지 저장이 아니다
        if (e.key !== 'Enter' || composing(e)) return;
        e.preventDefault();
        ok();
      });
      fld.addEventListener('input', () => {
        err!.classList.remove('oui-on');
        fld.classList.remove('oui-bad');
      });
    }

    l.escape = cancel;
    // ① 배경으로 닫는 것은 정보 창(alert)만 — 확인창·입력창은 흔들어 알리기만 한다
    guardBackdrop(back, () => {
      if (cfg.kind === 'alert') finish(undefined);
      else nudge(box);
    });

    const a = document.activeElement as HTMLElement | null;
    l.restoreTo = a && a !== document.body ? a : null;
    back.style.zIndex = String(Math.max(Z_BASE, highestZ() + Z_STEP)); // ⑥
    (document.body || document.documentElement).appendChild(back);
    pushLayer(l);

    // 지우는 일처럼 위험한 확인창은 '취소' 에 먼저 손이 가게 둔다.
    l.initial = field || (cfg.focusCancel && cancelBtn ? cancelBtn : okBtn);
    focusInto(l);
    if (field) {
      try {
        field.select();
      } catch {
        /* 선택을 못 해도 입력은 된다 */
      }
    }
  });
}

export interface AlertOptions {
  title?: string;
  okText?: string;
  tone?: 'ok' | 'bad' | 'warn' | 'info';
  bad?: boolean;
}
export interface ConfirmOptions {
  title?: string;
  okText?: string;
  cancelText?: string;
  /** 빨간 버튼 + 포커스는 '취소' */
  danger?: boolean;
  tone?: Tone | null;
}
export interface PromptOptions {
  title?: string;
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
  requiredText?: string;
  numeric?: boolean;
  maxLength?: number;
  okText?: string;
  cancelText?: string;
}

/** 알림 창 — 브라우저 alert() 대신 */
function uiAlert(message: string, opts: AlertOptions = {}): Promise<void> {
  return openDialog({
    kind: 'alert',
    role: 'alertdialog',
    tone: opts.tone || (opts.bad ? 'bad' : null),
    title: opts.title,
    message,
    okText: opts.okText,
  }) as Promise<void>;
}

/** 확인창 — 브라우저 confirm() 대신. `if (!(await OneUI.confirm('지울까요?', {danger:true}))) return;` */
function uiConfirm(message: string, opts: ConfirmOptions = {}): Promise<boolean> {
  return openDialog({
    kind: 'confirm',
    role: 'alertdialog',
    tone: opts.tone !== undefined ? opts.tone : opts.danger ? 'bad' : 'ask',
    title: opts.title,
    message,
    okText: opts.okText,
    cancelText: opts.cancelText,
    okClass: opts.danger ? 'danger' : 'primary',
    focusCancel: !!opts.danger,
  }) as Promise<boolean>;
}

/** 한 줄 입력창 — 브라우저 prompt() 대신. 취소하면 null */
function uiPrompt(message: string, opts: PromptOptions = {}): Promise<string | null> {
  return openDialog({
    kind: 'prompt',
    role: 'dialog',
    title: opts.title,
    message,
    okText: opts.okText,
    cancelText: opts.cancelText,
    field: {
      value: opts.defaultValue,
      placeholder: opts.placeholder,
      numeric: !!opts.numeric,
      maxLength: opts.maxLength,
      required: !!opts.required,
      requiredText: opts.requiredText,
    },
  }) as Promise<string | null>;
}

/** ② 입력 모달을 닫기 전에 묻는 말. 위험 확인창이라 Enter 는 '계속 작성' 이다. */
function confirmDiscard(): Promise<boolean> {
  return uiConfirm('입력한 내용은 저장되지 않습니다.', {
    title: '저장하지 않고 닫을까요?',
    okText: '닫기',
    cancelText: '계속 작성',
    danger: true,
  });
}

// ── 한글 검색 ────────────────────────────────────────────────────────
// [초성] 낱자는 그 자리 글자의 초성과 견준다 — 'ㅊㅎㅅ' 도, '최ㅎ' 도 '최호석' 에 맞는다.
// [겹자음] 입력기가 'ㄱ'+'ㅅ' 을 'ㄳ' 한 글자로 합치므로 견주기 전에 둘로 푼다.
const LEADS = 'ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ';
const SPLIT: Record<string, string> = {
  ㄳ: 'ㄱㅅ', ㄵ: 'ㄴㅈ', ㄶ: 'ㄴㅎ', ㄺ: 'ㄹㄱ', ㄻ: 'ㄹㅁ', ㄼ: 'ㄹㅂ',
  ㄽ: 'ㄹㅅ', ㄾ: 'ㄹㅌ', ㄿ: 'ㄹㅍ', ㅀ: 'ㄹㅎ', ㅄ: 'ㅂㅅ',
};
const SPLIT_RE = /[ㄳㄵㄶㄺㄻㄼㄽㄾㄿㅀㅄ]/g;
const JAMO_RE = /[ㄱ-ㅎ]/;

function normKo(s: unknown): string {
  let v = String(s == null ? '' : s);
  try {
    v = v.normalize('NFC');
  } catch {
    /* 옛 브라우저 */
  }
  return v
    .replace(/\s+/g, '')
    .toLowerCase()
    .replace(SPLIT_RE, (c) => SPLIT[c] ?? c);
}
function leadOf(ch: string): string {
  const c = ch.charCodeAt(0);
  return c >= 0xac00 && c <= 0xd7a3 ? LEADS.charAt(Math.floor((c - 0xac00) / 588)) : '';
}

/** 검색어가 글에 들어 있는가. 공백·대소문자 무시, 초성·겹자음 지원. */
function matchKo(query: unknown, text: unknown): boolean {
  const q = normKo(query);
  if (!q) return true;
  const t = normKo(text);
  if (t.indexOf(q) !== -1) return true;
  if (!JAMO_RE.test(q)) return false;
  const n = q.length;
  for (let i = 0; i + n <= t.length; i++) {
    let k = 0;
    for (; k < n; k++) {
      const qc = q.charAt(k);
      const tc = t.charAt(i + k);
      if (qc === tc) continue;
      if (JAMO_RE.test(qc) && leadOf(tc) === qc) continue; // 낱자는 초성과 견준다
      break;
    }
    if (k === n) return true;
  }
  return false;
}

/** 낱자(ㄱ~ㅎ)가 섞인 검색어인가 — 서버 ilike 로는 못 찾는다. */
function hasJamo(query: unknown): boolean {
  return JAMO_RE.test(normKo(query));
}

/** 사람 표시 '이름/부서'. */
function personLabel(name: unknown, deptName: unknown): string {
  const n = String(name == null ? '' : name).trim();
  const dp = String(deptName == null ? '' : deptName).trim();
  if (!n) return '';
  return dp ? n + '/' + dp : n;
}

export const OneUI = {
  version: ONE_UI_VERSION,
  alert: uiAlert,
  confirm: uiConfirm,
  prompt: uiPrompt,
  confirmDiscard,
  guardBackdrop,
  bindModal,
  formState,
  hasOpen: (): boolean => !!topLayer(),
  matchKo,
  hasJamo,
  personLabel,
};
